import clsx from 'clsx';
import React from 'react';

import { InboxPageContext } from 'src/context/inboxPageContext';
import { useContextBar } from 'src/hooks/useContextBar';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import {
  INBOX_NOTIFICATIONS_PAGE_SIZE,
  SIDEBAR_PAGES_TO_LOAD,
  useGetInboxNotificationsQuery,
} from 'src/services/api/inboxApi';

import { InboxContent } from './InboxContent';
import { InboxSidebar, InboxSidebarItemData } from './InboxSidebar';

export const InboxPage = () => {
  const { open: contextBarOpen } = useContextBar();

  const [pagesToLoad, setPagesToLoad] = React.useState(SIDEBAR_PAGES_TO_LOAD);
  const [hasMoreNotifications, setHasMoreNotifications] = React.useState(true);
  const pendingLoadMoreRequestRef = React.useRef<{
    pagesToLoad: number;
    previousNotificationCount: number;
  } | null>(null);

  const {
    data: inboxNotifications = [],
    isLoading: areNotificationsLoading,
    isFetching: areNotificationsFetching,
  } = useGetInboxNotificationsQuery(pagesToLoad);

  const [selectedNotification, setSelectedNotification] =
    React.useState<InboxSidebarItemData | null>(null);

  const loadMoreNotifications = React.useCallback(() => {
    if (
      !hasMoreNotifications ||
      areNotificationsFetching ||
      pendingLoadMoreRequestRef.current
    ) {
      return;
    }

    const nextPagesToLoad = pagesToLoad + 1;
    pendingLoadMoreRequestRef.current = {
      pagesToLoad: nextPagesToLoad,
      previousNotificationCount: inboxNotifications.length,
    };
    setPagesToLoad(nextPagesToLoad);
  }, [
    hasMoreNotifications,
    areNotificationsFetching,
    pagesToLoad,
    inboxNotifications.length,
  ]);

  React.useEffect(() => {
    if (
      areNotificationsFetching ||
      pagesToLoad !== SIDEBAR_PAGES_TO_LOAD ||
      pendingLoadMoreRequestRef.current
    ) {
      return;
    }

    setHasMoreNotifications(
      inboxNotifications.length >= INBOX_NOTIFICATIONS_PAGE_SIZE,
    );
  }, [areNotificationsFetching, pagesToLoad, inboxNotifications.length]);

  React.useEffect(() => {
    if (areNotificationsFetching || !pendingLoadMoreRequestRef.current) {
      return;
    }

    const pendingLoadRequest = pendingLoadMoreRequestRef.current;
    if (pendingLoadRequest.pagesToLoad !== pagesToLoad) {
      return;
    }

    const loadedNotificationsCount =
      inboxNotifications.length - pendingLoadRequest.previousNotificationCount;
    const didLoadFullNotificationsPage =
      loadedNotificationsCount >= INBOX_NOTIFICATIONS_PAGE_SIZE;
    setHasMoreNotifications(didLoadFullNotificationsPage);
    pendingLoadMoreRequestRef.current = null;
  }, [areNotificationsFetching, inboxNotifications.length, pagesToLoad]);

  const isInitialNotificationsLoading =
    pagesToLoad === SIDEBAR_PAGES_TO_LOAD &&
    (areNotificationsLoading ||
      (areNotificationsFetching && inboxNotifications.length === 0));

  React.useEffect(() => {
    if (
      isInitialNotificationsLoading ||
      !hasMoreNotifications ||
      areNotificationsFetching ||
      pendingLoadMoreRequestRef.current
    ) {
      return;
    }

    loadMoreNotifications();
  }, [
    isInitialNotificationsLoading,
    hasMoreNotifications,
    areNotificationsFetching,
    loadMoreNotifications,
  ]);

  const inboxPageContextValue = React.useMemo(
    () => ({
      selectedNotification,
      setSelectedNotification,
      pagesToLoad,
      hasMoreNotifications,
      isLoadingMoreNotifications:
        areNotificationsFetching && pagesToLoad > SIDEBAR_PAGES_TO_LOAD,
      loadMoreNotifications,
    }),
    [
      selectedNotification,
      setSelectedNotification,
      pagesToLoad,
      hasMoreNotifications,
      areNotificationsFetching,
      loadMoreNotifications,
    ],
  );

  return (
    <InboxPageContext.Provider value={inboxPageContextValue}>
      <LoadingWrapper
        hideContentWhileLoading
        isLoading={isInitialNotificationsLoading}
      >
        <div
          className={clsx('h-full flex', !contextBarOpen && 'overflow-hidden')}
        >
          <InboxSidebar />
          <InboxContent />
        </div>
      </LoadingWrapper>
    </InboxPageContext.Provider>
  );
};
